const BOARD_SIZE = 50000;
const TARGET_WIDTH = 1140;
const ZOOM_STEP = 0.1;

let height, width;

export class Viewport
{
    static zoom;

    static #element;

    static getElementOffset (mouseX, mouseY, viewportOffsetX, viewportOffsetY)
    {
        const rect = this.#element.querySelector('div').getBoundingClientRect();

        return {
            x: (mouseX - rect.left) / this.zoom - (parseFloat(viewportOffsetX) || 0),
            y: (mouseY - rect.top) / this.zoom - (parseFloat(viewportOffsetY) || 0),
        };
    }

    static getScrollPosition ()
    {
        return {
            left: this.#element.scrollLeft,
            top: this.#element.scrollTop,
        };
    }

    static getViewportCoordinates (mouseX, mouseY, magnetOffsetX, magnetOffsetY)
    {
        const rect = this.#element.querySelector('div').getBoundingClientRect();

        return {
            x: Math.max(0, Math.min((mouseX - rect.left) / this.zoom - magnetOffsetX, BOARD_SIZE)),
            y: Math.max(0, Math.min((mouseY - rect.top) / this.zoom - magnetOffsetY, BOARD_SIZE)),
        };
    }

    static init (element)
    {
        this.#element = element;

        height = element.clientHeight;
        width = element.clientWidth;

        document.body.style.setProperty('--fridge-size', `${BOARD_SIZE}px`);

        document.addEventListener('DOMContentLoaded', () => this.#reset());
        window.addEventListener('load', () => this.#reset());
        window.addEventListener('resize', () => this.#reset());

        const buttonGroup = document.querySelector('.button-group');
        buttonGroup.querySelector(':nth-child(1)').addEventListener('click', () => {
            const viewportCenterX = this.#element.clientWidth / 2;
            const viewportCenterY = this.#element.clientHeight / 2;
            this.zoomAndPan(this.zoom - 0.15, viewportCenterX, viewportCenterY)
        });

        buttonGroup.querySelector(':nth-child(2)').addEventListener('click', () => {
            this.#panToMiddle();
        });

        buttonGroup.querySelector(':nth-child(3)').addEventListener('click', () => {
            const viewportCenterX = this.#element.clientWidth / 2;
            const viewportCenterY = this.#element.clientHeight / 2;
            this.zoomAndPan(this.zoom + 0.15, viewportCenterX, viewportCenterY)
        });

        // Hijack the cmd/ctrl +/- event and perform our own zoom
        window.addEventListener('keydown', (e) => {
            if ((!e.metaKey && !e.ctrlKey) || !['-', '+', '='].includes(e.key)) {
                return;
            }

            e.preventDefault();
            this.zoomAndPan(this.zoom + (e.key === '-' ? -ZOOM_STEP : ZOOM_STEP));
        }, { passive: false });
    }

    static scrollTo (left, top)
    {
        this.#element.scrollLeft = left;
        this.#element.scrollTop = top;
    }

    static setCursor (cursor)
    {
        this.#element.style.cursor = cursor;
    }

    static zoomAndPan (newZoom, targetX = width / 2, targetY = height / 2)
    {
        // Make sure we don't go overboard with the zoom level
        newZoom = Math.max(0.15, Math.min(newZoom, 3));
        if (newZoom === this.zoom) {
            return;
        }

        // Figure out the scroll position based on the old zoom
        const snapshotX = (this.#element.scrollLeft + targetX) / this.zoom;
        const snapshotY = (this.#element.scrollTop + targetY) / this.zoom;

        this.#setZoom(newZoom);

        // Recalculate the scroll position based on the new zoom
        this.#element.scrollLeft = (snapshotX * newZoom) - targetX;
        this.#element.scrollTop = (snapshotY * newZoom) - targetY;

        this.#element.scrollTo({
            behavior: 'smooth',
            left: (snapshotX * newZoom) - targetX,
            right: (snapshotY * newZoom) - targetY,
        })
    }

    static #panToMiddle ()
    {
        this.#element.scrollTo({
            behavior: 'smooth',
            left: (BOARD_SIZE / 2 * this.zoom) - (width / 2),
            top: (BOARD_SIZE / 2 * this.zoom) - (height / 2),
        });
    }

    static #reset (resetZoom = false)
    {
        let zoom = width * 0.9 / TARGET_WIDTH;
        zoom = Math.max(0.1, Math.min(zoom, 3));

        const left = (BOARD_SIZE / 2 * zoom) - (width / 2);
        const top = (BOARD_SIZE / 2 * zoom) - (height / 2);

        this.#setZoom(zoom);
        this.#element.scrollTo({ left, top, behavior: 'instant' });
    }

    static #setZoom (zoom)
    {
        this.#element.style.setProperty('--zoom', zoom);
        this.zoom = zoom;
    }
}
